/**
 * Pure data + gating for the daily "Pre-Trading Checklist" popup.
 *
 * The React component (PreTradingChecklistModal) owns the timers and DOM;
 * everything decidable without a browser lives here so it can be unit
 * tested. The market-hours / holiday / half-day awareness comes from
 * marketTime.getMarketSession (isDuringMarketHours), so this module stays
 * a thin, deterministic policy layer.
 */

/** The checklist questions, in display order (Warrior Trading's
 *  pre-trading reflection list — the mockup Dan supplied). */
export const PRE_TRADING_CHECKLIST_ITEMS: readonly string[] = [
  'Did I sleep well?',
  'Do I feel emotionally centered and grounded?',
  'How has trading been going during the last few weeks?',
  'How was the market yesterday?',
  'How was my trading yesterday?',
  'Were there strong stocks yesterday? If yes, what are they doing now?',
  'How is the overall market looking this morning?',
  'How do the scanners look this morning?',
  'Do I sense FOMO in the market?',
];

/** Seconds the Dismiss button stays locked after the popup appears. The
 *  countdown is component state (never persisted), so a page refresh
 *  restarts it from scratch — you can't refresh-skip the review. */
export const CHECKLIST_DISMISS_LOCK_SECONDS = 15;

/** localStorage key → the ET date (YYYY-MM-DD) the checklist was last
 *  dismissed on. Only written on an actual dismiss (after the lock), so a
 *  refresh before dismissing re-shows it with a fresh timer. */
export const CHECKLIST_DISMISSED_KEY = 'pretradingChecklist:dismissedDate';

/** window CustomEvent the configurations toggle dispatches when the user
 *  turns the checklist ON, so the (always-mounted) modal shows immediately
 *  — bypassing the market-hours / dismissed-today gate for instant feedback
 *  and a preview any time. */
export const CHECKLIST_FORCE_SHOW_EVENT = 'pretradingChecklist:forceShow';

/** BroadcastChannel name for cross-tab dismiss sync. When the checklist is
 *  dismissed in one tab we post `{ type: 'dismiss' }` here so every other open
 *  tab hides its (already-shown) modal in lockstep — mirrors the buy/sell
 *  pending-review popup's 'pending-review-queue' channel. The dismissed-today
 *  flag itself lives in localStorage (shared across tabs), so this only needs
 *  to live-hide siblings; their next gate eval keeps it hidden. */
export const CHECKLIST_CHANNEL = 'pretrading-checklist';

/** True when the stored dismissed-date equals today's ET trading date —
 *  i.e. the user already cleared the checklist today. */
export function wasDismissedOn(storedDate: string | null, etDate: string): boolean {
  return storedDate != null && storedDate === etDate;
}

export interface ChecklistGateInput {
  /** User is authenticated. */
  loggedIn: boolean;
  /** User preference (default ON). */
  enabled: boolean;
  /** getMarketSession() === 'regular' — holiday/half-day aware, so this is
   *  false on weekends, full holidays, before 9:30 ET, and after the
   *  (possibly early) close. */
  duringMarketHours: boolean;
  /** Already dismissed for today's ET date. */
  dismissedToday: boolean;
}

/**
 * The single gate: should the popup be visible right now? Pure AND of the
 * four conditions. The component evaluates this on mount and on a polling
 * interval (so it also fires the moment the bell rings if the tab was
 * already open during pre-market).
 */
export function shouldShowChecklist(i: ChecklistGateInput): boolean {
  return i.loggedIn && i.enabled && i.duringMarketHours && !i.dismissedToday;
}
